package fr.manyverse.app.presentation.state

/**
 * Generic sealed class for representing asynchronous UI states.
 * This replaces the numerous duplicated sealed classes across ViewModels
 * (CharacterListUiState, CreateCharacterUiState, GameActionUiState, etc.)
 *
 * Usage:
 * ```kotlin
 * private val _state = MutableStateFlow<AsyncUiState<List<Character>>>(AsyncUiState.Idle)
 * val state: StateFlow<AsyncUiState<List<Character>>> = _state.asStateFlow()
 * ```
 *
 * @param T The type of data on success
 */
sealed class AsyncUiState<out T> {
    /**
     * Initial state, no operation has been started.
     */
    data object Idle : AsyncUiState<Nothing>()

    /**
     * Operation is in progress.
     */
    data object Loading : AsyncUiState<Nothing>()

    /**
     * Operation completed successfully with data.
     *
     * @param data The result data
     */
    data class Success<T>(val data: T) : AsyncUiState<T>()

    /**
     * Operation failed with an error message.
     *
     * @param message The error message to display to the user
     */
    data class Error(val message: String) : AsyncUiState<Nothing>()

    /**
     * Returns true if this state is [Loading].
     */
    val isLoading: Boolean
        get() = this is Loading

    /**
     * Returns true if this state is [Success].
     */
    val isSuccess: Boolean
        get() = this is Success

    /**
     * Returns true if this state is [Error].
     */
    val isError: Boolean
        get() = this is Error

    /**
     * Returns true if this state is [Idle].
     */
    val isIdle: Boolean
        get() = this is Idle

    /**
     * Returns the data if this state is [Success], or null otherwise.
     */
    fun getOrNull(): T? = (this as? Success)?.data

}

/**
 * Type alias for operations that don't return data (e.g., delete, update).
 * Success state will contain Unit.
 */
typealias ActionUiState = AsyncUiState<Unit>
